/**
 * Pagination Component
 * Reusable pagination controls with page numbers, prev/next, and items per page selector
 */

import { useId } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface PaginationProps {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  entityName: string;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  showLimitSelector?: boolean;
  isLoading?: boolean;
}

const LIMIT_OPTIONS = [10, 25, 50, 100];

export const Pagination = ({
  page,
  limit,
  total,
  totalPages,
  entityName,
  onPageChange,
  onLimitChange,
  showLimitSelector = true,
  isLoading = false,
}: PaginationProps) => {
  const limitSelectId = useId();
  // Calculate showing range
  const startItem = total === 0 ? 0 : (page - 1) * limit + 1;
  const endItem = Math.min(page * limit, total);

  // Generate page numbers with ellipsis pattern: 1 ... 4 5 6 ... 10
  const getPageNumbers = (): (number | 'ellipsis')[] => {
    if (totalPages <= 7) {
      // Show all pages if 7 or fewer
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const pages: (number | 'ellipsis')[] = [];

    // Always show first page
    pages.push(1);

    if (page <= 3) {
      // Near start: 1 2 3 4 5 ... 10
      pages.push(2, 3, 4, 5);
      pages.push('ellipsis');
      pages.push(totalPages);
    } else if (page >= totalPages - 2) {
      // Near end: 1 ... 6 7 8 9 10
      pages.push('ellipsis');
      pages.push(totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
    } else {
      // Middle: 1 ... 4 5 6 ... 10
      pages.push('ellipsis');
      pages.push(page - 1, page, page + 1);
      pages.push('ellipsis');
      pages.push(totalPages);
    }

    return pages;
  };

  const pageNumbers = getPageNumbers();

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 py-4">
      {/* Left side: Showing X-Y of Z (hidden on mobile) */}
      <div className="hidden sm:block text-sm text-meta">
        {total > 0 ? (
          <>
            {startItem}–{endItem} of {total} {entityName}
          </>
        ) : (
          <>No {entityName} found</>
        )}
      </div>

      {/* Mobile: Simple page indicator */}
      <div className="sm:hidden text-sm text-meta">
        Page {page} of {totalPages}
      </div>

      {/* Right side: Page controls */}
      <div className="flex items-center gap-2">
        {/* Items per page selector (hidden on mobile) */}
        {showLimitSelector && (
          <div className="hidden sm:flex items-center gap-2">
            <label htmlFor={limitSelectId} className="text-sm text-meta">Rows per page:</label>
            <select
              id={limitSelectId}
              value={limit}
              onChange={(e) => onLimitChange(Number(e.target.value))}
              className="border border-strong rounded px-2 py-1 text-sm text-heading bg-surface focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={isLoading}
            >
              {LIMIT_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Previous button */}
        <button
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1 || isLoading}
          className="p-2 rounded border border-strong text-body hover:bg-surface-hover disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Previous page"
        >
          <ChevronLeft className="h-icon-sm w-icon-sm" />
        </button>

        {/* Page numbers (hidden on mobile) */}
        <div className="hidden sm:flex items-center gap-1">
          {pageNumbers.map((pageNum, index) =>
            pageNum === 'ellipsis' ? (
              <span key={`ellipsis-${index}`} className="px-2 text-meta">
                ...
              </span>
            ) : (
              <button
                key={pageNum}
                onClick={() => onPageChange(pageNum)}
                disabled={isLoading}
                className={`min-w-[32px] h-8 px-2 rounded text-sm font-medium transition-colors ${
                  pageNum === page
                    ? 'bg-blue-600 text-white'
                    : 'border border-strong text-body hover:bg-surface-hover'
                } disabled:opacity-50`}
              >
                {pageNum}
              </button>
            )
          )}
        </div>

        {/* Next button */}
        <button
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages || isLoading}
          className="p-2 rounded border border-strong text-body hover:bg-surface-hover disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Next page"
        >
          <ChevronRight className="h-icon-sm w-icon-sm" />
        </button>
      </div>
    </div>
  );
};
